import {
  useGetInvoiceId,
  useUpdateInvoice,
  useUpdateInvoiceAdditional,
} from '@/core/domain/invoice/invoice/invoice.hooks';
import {
  InvoiceCreateDTO,
  InvoiceGeneralFormValues,
  UpdateInvoiceAdditionalDTO,
} from '@/core/domain/invoice/invoice/Invoice.types';
import { InvoiceTransportRequest } from '@/core/domain/invoice/invoiceTransport/invoiceCarrier.types';
import { useDefaultFormErrorNotification, useServiceError } from '@/hooks';
import { Form } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { AxiosError } from 'axios';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  useInvoiceEditNFc,
  useInvoiceEditNFe,
  useInvoiceEditNFs,
} from '@/core/domain/invoice/invoices/invoices.hooks';
import {
  InvoiceSituation,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import {
  InvoiceSettingConfigNFc,
  InvoiceSettingConfigNFe,
  InvoiceSettingConfigNFs,
  InvoiceSettingsData,
} from '@/core/domain/invoice/settings/Settings.types';
import { useListInvoiceItem } from '@/core/domain/invoice/invoiceItem/invoiceItem.hook';
import { useGetCompanyProvider } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { CompanyProvider } from '@/core/domain/enterprise/Companies/Companies.types';
import {
  useCreateInvoiceTransport,
  useUpdateInvoiceTransport,
} from '@/core/domain/invoice/invoiceTransport/invoiceCarrier.hooks';
import { InvoiceItemList } from '@/core/domain/invoice/invoiceItem/invoiceItem.types';
import { CancellationCodeResponse } from '@/core/domain/nfs/NfseController/NfseController.types';
import { useCancellationCode } from '@/core/domain/nfs/NfseController/NfseController.hooks';
import { getInvoiceDataToUpdate } from './getInvoiceDataToSend';

type Props = PropsWithChildren<unknown>;

interface ValuesCalculated {
  totalItems: number;
  totalSafe: number;
  totalFreight: number;
  otherValue: number;
  totalDiscount: number;
  totalNote: number;
  totalWithheldValue: number;
  totalPisWithheldValue: number;
  totalCofinsWithheldValue: number;
  totalCsllWithheldValue: number;
  totalInssWithheldValue: number;
  totalIrrfWithheldValue: number;
  totalCppWithheldValue: number;
  totalOtherWithholdings: number;
}

export type Forms = {
  invoiceGeneral?: InvoiceGeneralFormValues;
  invoiceRecipient?: InvoiceGeneralFormValues;
  invoiceTransport?: InvoiceTransportRequest;
  invoiceAditionalData?: UpdateInvoiceAdditionalDTO;
};

type InstanceForms = {
  invoiceGeneral?: FormInstance<InvoiceGeneralFormValues>;
  invoiceRecipient?: FormInstance<InvoiceGeneralFormValues>;
  invoiceTransport?: FormInstance<InvoiceTransportRequest>;
  invoiceAditionalData?: FormInstance<UpdateInvoiceAdditionalDTO>;
};

type InvoiceFormContextProps = {
  isLoading: boolean;
  forms: Forms;
  invoiceId: string;
  disabled: boolean;
  disabledFields: boolean;
  loadActive: boolean;
  setLoadActive: (isActive: boolean) => void;
  handleSubmit: () => Promise<void>;
  instanceForms: InstanceForms;
  invoice: {
    data?: InvoiceCreateDTO;
    isLoading: boolean;
    dataUpdatedAt: number;
  };
  invoiceSettingNfe?: InvoiceSettingConfigNFe;
  invoiceSettingNfc?: InvoiceSettingConfigNFc;
  invoiceSettingNfs?: InvoiceSettingConfigNFs;
  invoiceValues?: ValuesCalculated;
  companyProvider?: CompanyProvider;
  invoiceSetting?: InvoiceSettingsData;
  useInternalInvoiceApiNfe: boolean;
  useInternalInvoiceApiNfc: boolean;
  useInternalInvoiceApiNfs: boolean;
  invoiceItems: {
    data?: InvoiceItemList[];
    isLoading: boolean;
  };
  isNfe: boolean;
  isNfc: boolean;
  isNfs: boolean;
  isInternalApiIssued: boolean;
  cancellationCode?: CancellationCodeResponse[];
  activeNfseNacional?: boolean;
};
const InvoiceFormContext = createContext({} as InvoiceFormContextProps);
export const useInvoiceFormContext = () => useContext(InvoiceFormContext);

const InvoiceFormProvider: React.FC<Props> = ({ children }) => {
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { params } = useRouteMatch<{ invoiceId: string }>();
  const [forms, setForms] = useState<Forms>({});
  const [invoiceId, setInvoiceId] = useState<string>('');
  const [loadActive, setLoadActive] = useState<boolean>(true);
  const [invoiceValues, setInvoiceValues] = useState<ValuesCalculated>({
    totalItems: 0,
    totalSafe: 0,
    totalFreight: 0,
    totalDiscount: 0,
    otherValue: 0,
    totalNote: 0,
    totalWithheldValue: 0,
    totalPisWithheldValue: 0,
    totalCofinsWithheldValue: 0,
    totalCsllWithheldValue: 0,
    totalInssWithheldValue: 0,
    totalIrrfWithheldValue: 0,
    totalCppWithheldValue: 0,
    totalOtherWithholdings: 0,
  });

  const { data: invoiceItems, isLoading: itemsIsLoading } = useListInvoiceItem({
    invoiceId: Number(invoiceId),
  });

  const disabled = !Object.keys(forms).some((key) => !!key);

  const [instanceForms, setInstanceForms] = useState<InstanceForms>({});

  const {
    data: invoice,
    isLoading: invoiceIsLoading,
    dataUpdatedAt,
  } = useGetInvoiceId(Number(invoiceId));

  const invoiceDataToUpdate = getInvoiceDataToUpdate(invoice);

  const disabledFields =
    invoice?.situation !== InvoiceSituation.IN_TYPING &&
    invoice?.situation !== InvoiceSituation.VALIDATED;

  const { data: companyProvider } = useGetCompanyProvider(invoice?.companyId);

  const { data: invoiceSetting } = useInvoiceSettingsByCompany(
    Number(invoice?.companyId),
  );

  const isInternalApiIssued = !!invoice?.isInternalApiIssued;

  const isNfe = invoice?.invoiceType === InvoiceType.NFE;
  const isNfc = invoice?.invoiceType === InvoiceType.NFC;
  const isNfs = invoice?.invoiceType === InvoiceType.NFS;

  const useInternalInvoiceApiNfe = !!invoiceSetting?.useInternalInvoiceApiNfe;
  const useInternalInvoiceApiNfc = !!invoiceSetting?.useInternalInvoiceApiNfc;
  const useInternalInvoiceApiNfs = !!invoiceSetting?.useInternalInvoiceApiNfs;

  const { data: cancellationCode } = useCancellationCode(Number(invoiceId));

  useEffect(() => {
    if (invoiceItems) {
      const totalItems = invoiceItems.reduce((acc, cur) => acc + cur.total, 0);

      const totalSafe = invoiceItems.reduce(
        (acc, cur) => acc + (cur.insuranceValue || 0),
        0,
      );

      const totalFreight = invoiceItems.reduce(
        (acc, cur) => acc + (cur.freightValue || 0),
        0,
      );

      const otherValue = invoiceItems.reduce(
        (acc, cur) => acc + (cur.otherValue || 0),
        0,
      );

      const totalDiscount = invoiceItems.reduce(
        (acc, cur) =>
          acc +
          (cur.valueDiscount || 0) +
          (cur.unconditionalDiscount || 0) +
          (cur.conditionalDiscount || 0),
        0,
      );

      const totalWithheldValue = invoiceItems.reduce(
        (acc, cur) => acc + (cur.withheldValue || 0),
        0,
      );

      const totalPisWithheldValue = invoice?.retentionPisCofins
        ? invoiceItems.reduce(
            (acc, cur) =>
              acc + ((cur.pisWithheldValue || 0) + (cur.pisValue || 0)),
            0,
          )
        : 0;

      const totalCofinsWithheldValue = invoice?.retentionPisCofins
        ? invoiceItems.reduce(
            (acc, cur) =>
              acc + ((cur.cofinsWithheldValue || 0) + (cur.cofinsValue || 0)),
            0,
          )
        : 0;

      const totalCsllWithheldValue = invoiceItems.reduce(
        (acc, cur) => acc + (cur.csllWithheldValue || 0),
        0,
      );

      const totalInssWithheldValue = invoiceItems.reduce(
        (acc, cur) => acc + (cur.inssWithheldValue || 0),
        0,
      );

      const totalIrrfWithheldValue = invoiceItems.reduce(
        (acc, cur) => acc + (cur.irrfWithheldValue || 0),
        0,
      );

      const totalCppWithheldValue = invoiceItems.reduce(
        (acc, cur) => acc + (cur.cppWithheldValue || 0),
        0,
      );

      const totalOtherWithholdings = invoiceItems.reduce(
        (acc, cur) => acc + (cur.otherWithholdings || 0),
        0,
      );

      setInvoiceValues({
        totalItems: totalItems,
        totalSafe: totalSafe || 0,
        totalFreight: totalFreight || 0,
        totalDiscount: totalDiscount || 0,
        otherValue: otherValue || 0,
        totalNote:
          (useInternalInvoiceApiNfe && isNfe) ||
          (useInternalInvoiceApiNfc && isNfc) ||
          (useInternalInvoiceApiNfs && isNfs)
            ? totalItems
            : totalItems +
              totalSafe +
              totalFreight +
              otherValue -
              (totalWithheldValue || 0),
        totalWithheldValue: totalWithheldValue || 0,
        totalPisWithheldValue: totalPisWithheldValue || 0,
        totalCofinsWithheldValue: totalCofinsWithheldValue || 0,
        totalCsllWithheldValue: totalCsllWithheldValue || 0,
        totalInssWithheldValue: totalInssWithheldValue || 0,
        totalIrrfWithheldValue: totalIrrfWithheldValue || 0,
        totalCppWithheldValue: totalCppWithheldValue || 0,
        totalOtherWithholdings: totalOtherWithholdings || 0,
      });
    }
  }, [invoiceItems, invoice]);

  const updateInvoice = useUpdateInvoice();
  const invoiceEditNFe = useInvoiceEditNFe();
  const invoiceEditNFc = useInvoiceEditNFc();
  const invoiceEditNFs = useInvoiceEditNFs();
  const updateInvoiceAditionalData = useUpdateInvoiceAdditional();
  const createTransport = useCreateInvoiceTransport();
  const updateTransport = useUpdateInvoiceTransport();

  const isLoading =
    updateInvoice.isLoading ||
    invoiceEditNFe.isLoading ||
    invoiceEditNFc.isLoading ||
    invoiceEditNFs.isLoading ||
    updateInvoiceAditionalData.isLoading ||
    createTransport.isLoading ||
    updateTransport.isLoading;

  const activeNfseNacional = invoiceSetting?.configNfs?.activeNfseNacional;

  useServiceError(updateInvoice.error);

  useEffect(() => {
    setForms({});
    if (params.invoiceId) {
      setInvoiceId(params.invoiceId);
    }
  }, [params.invoiceId]);

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setInstanceForms((state) => ({ ...state, [name]: info.forms[name] }));
      setForms((state) => ({
        ...state,
        [name]: info.forms[name].getFieldsValue(),
      }));
    }
  }

  async function handleSubmit() {
    return new Promise<void>(async (res, rej) => {
      try {
        await Promise.all(
          Object.keys(instanceForms).map((key: unknown) =>
            instanceForms[key as keyof Forms]?.validateFields(),
          ),
        );

        if (forms.invoiceGeneral || forms.invoiceRecipient) {
          let recipient;
          let recipientValues;

          if (forms.invoiceRecipient) {
            const { recipient: recipientData, ...restRecipientValues } =
              forms.invoiceRecipient;
            recipient = recipientData;
            recipientValues = restRecipientValues;
          }
          const values = {
            ...forms.invoiceGeneral,
            ...recipient,
            ...recipientValues,
            publicAgency:
              recipient?.publicAgency || invoice?.publicAgency || undefined,
          };

          if (isNfe) {
            const obj = invoiceDataToUpdate.getNfeDataUpdate(values);

            if (obj) {
              await invoiceEditNFe.mutateAsync(obj);
            }
          }

          if (isNfc) {
            const obj = invoiceDataToUpdate.getNfcDataUpdate(values);

            if (obj) {
              await invoiceEditNFc.mutateAsync(obj);
            }
          }

          if (isNfs) {
            const obj = invoiceDataToUpdate.getNfsDataUpdate(values);

            if (obj) {
              await invoiceEditNFs.mutateAsync(obj);
            }
          }
        }

        if (forms.invoiceAditionalData) {
          await updateInvoiceAditionalData.mutateAsync({
            ...forms.invoiceAditionalData,
            invoiceId: Number(invoiceId),
          });
        }

        if (forms.invoiceTransport) {
          const obj = forms.invoiceTransport;
          const action = obj?.id ? updateTransport : createTransport;

          await action.mutateAsync({ invoiceId: Number(invoiceId), obj });

          setForms((prev) => {
            const updatedForms = { ...prev };
            delete updatedForms.invoiceTransport;
            return updatedForms;
          });
        }

        res();
      } catch (error: unknown) {
        const err = error as AxiosError;
        if (!err.isAxiosError) {
          sendFormErrorNotification(error);
        }
        rej(error);
      }
    });
  }
  return (
    <InvoiceFormContext.Provider
      value={{
        isLoading,
        forms,
        invoiceId: params.invoiceId,
        disabled,
        disabledFields,
        loadActive,
        setLoadActive,
        handleSubmit,
        instanceForms,
        invoice: {
          data: invoice,
          isLoading: invoiceIsLoading,
          dataUpdatedAt,
        },
        invoiceSettingNfe: invoiceSetting?.configNfe,
        invoiceSettingNfc: invoiceSetting?.configNfc,
        invoiceSettingNfs: invoiceSetting?.configNfs,
        invoiceSetting,
        invoiceValues,
        companyProvider,
        useInternalInvoiceApiNfe,
        useInternalInvoiceApiNfc,
        useInternalInvoiceApiNfs,
        invoiceItems: {
          data: invoiceItems,
          isLoading: itemsIsLoading,
        },
        isNfe,
        isNfc,
        isNfs,
        isInternalApiIssued,
        cancellationCode,
        activeNfseNacional,
      }}
    >
      <Form.Provider
        onFormFinish={handleSubmit}
        onFormChange={handleFormChange}
      >
        {children}
      </Form.Provider>
    </InvoiceFormContext.Provider>
  );
};
export default InvoiceFormProvider;
